"use client";

import {
  Button,
  FormNumber,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  toast,
  useDebounceCallback,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeftIcon,
  InfoIcon,
  RefreshCw,
  ScanBarcodeIcon,
  Trash2Icon,
  ZapIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Resolver } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopBranchOption,
  type ShopSellableCatalogRow,
  type ShopVerifiedLevel,
  shopCatalogApi,
  shopInventoryApi,
} from "@/modules/merchant/presentation/api/client";
import {
  useCreateStockTransferMutation,
  useLookupSellableByBarcodeMutation,
  usePosSerialLookupMutation,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopWarehouseLocationPickerRemote } from "@/modules/merchant/presentation/ui/ShopWarehouseLocationPicker";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const INVENTORY_RESERVE_ROUTE = "/app/shops/$shopId/inventory/reserve" as const;

const DRAFT_STORAGE_KEY = (shopId: string) =>
  `partzync-stock-transfer-draft:${shopId}`;

/** ຄືກັນກັບ POS — ບໍ່ກັ່ນຮູບແບບ barcode */
const TRANSFER_SCAN_MAX_LEN = 100;

const TransferLineSchema = z.object({
  shopProductSkuId: z.string(),
  qty: z.coerce.number().int().positive().max(999_999),
});

const TransferFormSchema = z.object({
  fromBranchId: z.string(),
  toBranchId: z.string(),
  toLocationId: z.string().optional(),
  lines: z.array(TransferLineSchema).max(100),
  note: z.string().max(2000).optional(),
});

type TransferFormValues = z.infer<typeof TransferFormSchema>;

function isUuid(s: string): boolean {
  return z.string().uuid().safeParse(s).success;
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-heading font-semibold text-card-foreground text-xs uppercase tracking-[0.14em]">
      {children}
    </h2>
  );
}

function LineAvailableQty({
  shopId,
  fromBranchId,
  shopProductSkuId,
  numberFmt,
}: {
  shopId: string | undefined;
  fromBranchId: string;
  shopProductSkuId: string;
  numberFmt: Intl.NumberFormat;
}) {
  const q = useQuery({
    queryKey: ["transfer-line-stock", shopId, fromBranchId, shopProductSkuId],
    queryFn: async () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.listStock(shopId, {
        limit: 1,
        offset: 0,
        q: undefined,
        branchId: fromBranchId,
        shopProductSkuId,
      });
    },
    enabled: Boolean(
      shopId && isUuid(fromBranchId) && isUuid(shopProductSkuId),
    ),
  });

  if (!shopProductSkuId || !isUuid(shopProductSkuId)) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (!isUuid(fromBranchId)) {
    return <span className="text-muted-foreground text-xs">ເລືອກສາຂາຕົ້ນ</span>;
  }
  if (q.isLoading) {
    return (
      <span className="inline-flex text-muted-foreground">
        <Loader />
      </span>
    );
  }
  if (q.isError) {
    return <span className="text-destructive text-xs">!</span>;
  }
  const row = q.data?.data[0];
  if (!row) {
    return (
      <span className="text-muted-foreground tabular-nums">
        {numberFmt.format(0)} ຊິ້ນ
      </span>
    );
  }
  if (!row.inventoryTracked || row.quantityOnHand == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const avail = Math.max(0, row.quantityOnHand - (row.quantityReserved ?? 0));
  return (
    <span className="text-card-foreground tabular-nums">
      {numberFmt.format(avail)} ຊິ້ນ
    </span>
  );
}

export function ShopInventoryTransferPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel: ShopVerifiedLevel | null =
    currentShop?.verifiedLevel ?? null;

  const canTransfer = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.transfer,
    verifiedLevel,
  );
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );
  const transferMut = useCreateStockTransferMutation(shopId);
  const lookup = useLookupSellableByBarcodeMutation(shopId);

  const transferForm = RHF.useForm<TransferFormValues>({
    resolver: zodResolver(TransferFormSchema) as Resolver<TransferFormValues>,
    defaultValues: {
      fromBranchId: "",
      toBranchId: "",
      toLocationId: "",
      lines: [],
      note: "",
    },
  });

  const {
    fields: lineFields,
    append: appendLine,
    remove: removeLine,
  } = useFieldArray({
    control: transferForm.control,
    name: "lines",
  });

  const barcodeFieldId = useId();
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const scanLockRef = useRef(false);
  const [barcode, setBarcode] = useState("");
  const [scanErrorOpen, setScanErrorOpen] = useState(false);
  const [failedBarcode, setFailedBarcode] = useState("");

  const branchQueryKey = useMemo((): string[] => {
    return ["shops", shopId ?? "", "inventory-transfer", "branch"];
  }, [shopId]);

  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);

  const dateLabel = useMemo(() => {
    return formatDateLocal(new Date());
  }, []);

  const fromBranchId = transferForm.watch("fromBranchId");
  const linesWatched = transferForm.watch("lines");

  const serialLookup = usePosSerialLookupMutation(
    shopId,
    isUuid(fromBranchId) ? fromBranchId : undefined,
  );

  const shopIdShort = shopId ? shopId.slice(0, 8) : "—";

  useEffect(() => {
    if (!scanErrorOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [scanErrorOpen]);

  useEffect(() => {
    const toId = transferForm.getValues("toBranchId");
    if (!toId || !isUuid(fromBranchId) || !isUuid(toId)) return;
    if (fromBranchId !== toId) return;
    transferForm.setValue("toBranchId", "", { shouldValidate: true });
  }, [fromBranchId, transferForm]);

  const loadDraft = useCallback(() => {
    if (!shopId) return null;
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY(shopId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== "object") return null;
      return parsed as Partial<TransferFormValues>;
    } catch {
      return null;
    }
  }, [shopId]);

  useEffect(() => {
    if (!shopId || !canTransfer) return;
    const d = loadDraft();
    if (!d) return;
    transferForm.reset({
      fromBranchId: typeof d.fromBranchId === "string" ? d.fromBranchId : "",
      toBranchId: typeof d.toBranchId === "string" ? d.toBranchId : "",
      lines: Array.isArray(d.lines) ? d.lines : [],
      note: typeof d.note === "string" ? d.note : "",
    });
  }, [shopId, canTransfer, loadDraft, transferForm.reset]);

  const submitTransfer = async (values: TransferFormValues) => {
    if (!isUuid(values.fromBranchId)) {
      toast.error("ເລືອກສາຂາຕົ້ນທາງ");
      return;
    }
    if (!isUuid(values.toBranchId)) {
      toast.error("ເລືອກສາຂາປາຍທາງ");
      return;
    }
    if (values.fromBranchId === values.toBranchId) {
      toast.error("ສາຂາຕົ້ນ ແລະ ປາຍທາງຕ້ອງຕ່າງກັນ");
      return;
    }
    const lines = values.lines.filter(
      (l) => l.shopProductSkuId.length > 0 && isUuid(l.shopProductSkuId),
    );
    if (lines.length === 0) {
      toast.error("ສະແກນ barcode ເພື່ອເພີ່ມຢ່າງນ້ອຍໜຶ່ງສິນຄ້າ");
      return;
    }
    try {
      await transferMut.mutateAsync({
        fromBranchId: values.fromBranchId,
        toBranchId: values.toBranchId,
        toLocationId: values.toLocationId?.trim() || undefined,
        lines: lines.map((l) => ({
          shopProductSkuId: l.shopProductSkuId,
          qty: l.qty,
        })),
        note: values.note?.trim() || undefined,
      });
      if (shopId) {
        try {
          localStorage.removeItem(DRAFT_STORAGE_KEY(shopId));
        } catch {
          /* ignore */
        }
      }
      transferForm.reset({
        fromBranchId: values.fromBranchId,
        toBranchId: values.toBranchId,
        lines: [],
        note: "",
      });
    } catch {
      /* toast ຢູ່ mutation */
    }
  };

  const addLineFromBarcodeScan = useCallback(
    (row: ShopSellableCatalogRow) => {
      if (row.trackingMode !== "none") {
        toast.error(
          "ໂອນສະເພາະ SKU ໂໝດລວມ — ບໍ່ຮອງຮັບ lot/serial; ໃຊ້ POS ສຳລັບສິນຄ້ານັ້ນ",
        );
        return;
      }
      const lines = transferForm.getValues("lines");
      const i = lines.findIndex((l) => l.shopProductSkuId === row.id);
      const nextQty = i >= 0 ? (lines[i]?.qty ?? 0) + 1 : 1;
      if (row.quantityOnHand !== null && nextQty > row.quantityOnHand) {
        toast.error("ສະຕ໋ອກບໍ່ພໍ (ຕາມຍອດທີ່ສະແດງໃນລາຍການຂາຍ)");
        return;
      }
      if (i >= 0) {
        transferForm.setValue(`lines.${i}.qty`, nextQty, {
          shouldValidate: true,
        });
      } else {
        appendLine({ shopProductSkuId: row.id, qty: 1 });
      }
    },
    [appendLine, transferForm],
  );

  const runScanForTransfer = useCallback(
    async (raw: string) => {
      const b = raw.trim();
      if (!b || !shopId) return;
      if (b.length > TRANSFER_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${TRANSFER_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      try {
        const result = await lookup.mutateAsync(b);
        if (result.meta.total === 0) {
          if (shopId && isUuid(fromBranchId)) {
            try {
              await serialLookup.mutateAsync(b);
              toast.error("ບໍ່ຮອງຮັບ serial/lot ໃນການໂອນ — ສະເພາະສິນຄ້າໂໝດລວມ");
              setBarcode("");
              queueMicrotask(() => barcodeInputRef.current?.focus());
              return;
            } catch {
              /* ບໍ່ພົບ serial */
            }
          }
          setFailedBarcode(b);
          setScanErrorOpen(true);
          return;
        }
        if (result.meta.total > 1) {
          toast.warning(`ພົບ ${result.meta.total} ລາຍການ — ເພີ່ມລາຍການທຳອິດ`);
        }
        const first = result.data[0];
        if (first) addLineFromBarcodeScan(first);
        setBarcode("");
        queueMicrotask(() => barcodeInputRef.current?.focus());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາລົ້ມເຫຼວ");
      } finally {
        scanLockRef.current = false;
      }
    },
    [shopId, fromBranchId, lookup, serialLookup, addLineFromBarcodeScan],
  );

  const runScanRef = useRef(runScanForTransfer);
  runScanRef.current = runScanForTransfer;

  const debouncedAutoScan = useDebounceCallback((value: string) => {
    const live = barcodeInputRef.current?.value ?? "";
    if (live !== value) return;
    void runScanRef.current(value);
  }, 90);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventoryTransfer(shopId)} />
      </Header>
      <Main className="bg-muted/35">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-semibold text-2xl text-foreground tracking-tight">
                ໂອນສະຕ໋ອກລະຫວ່າງສາຂາ
              </h1>
              <p className="mt-2 max-w-2xl text-muted-foreground text-sm leading-relaxed">
                ສ້າງ ແລະ ຢືນຢັນການເຄື່ອນຍ້າຍສະຕ໋ອກພາຍໃນ — ສະເພາະ SKU ໂໝດລວມ; ຫຼຸດ{" "}
                <span className="font-medium text-foreground">on_hand</span>{" "}
                ທີ່ຕົ້ນທາງ ແລະ ເພີ່ມທີ່ປາຍທາງໃນ transaction ດຽວ.
              </p>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="mb-2 -ml-2 gap-1 px-0 text-secondary hover:text-secondary"
              type="button"
              onClick={() => {
                if (!shopId) return;
                nav({ to: INVENTORY_ROUTE, params: { shopId } });
              }}
            >
              <ArrowLeftIcon className="size-4" aria-hidden />
              ກັບໄປສາງ
            </Button>
          </div>

          {canTransfer ? (
            <FormRoot<TransferFormValues>
              methods={transferForm}
              onSubmit={(v) => void submitTransfer(v)}
              className="space-y-8"
            >
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="space-y-4 lg:col-span-5">
                  <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
                    <CardTitle>ລາຍລະອຽດການໂອນ</CardTitle>
                    <div className="mt-5 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                          ສາຂາຕົ້ນທາງ
                        </Label>
                        {shopId ? (
                          <InfiniteCombobox<ShopBranchOption>
                            queryKey={branchQueryKey}
                            queryFn={async ({ search: q, pageParam }) => {
                              if (!shopId) {
                                return { items: [], nextPage: null };
                              }
                              return shopInventoryApi.lookupBranches(shopId, {
                                page: pageParam,
                                search: q.trim() || undefined,
                                limit: 20,
                              });
                            }}
                            preloadQueryFn={(id) =>
                              shopId
                                ? shopInventoryApi.getBranch(shopId, id)
                                : Promise.resolve(null)
                            }
                            getLabel={(item) => item.name}
                            getValue={(item) => item.id}
                            value={transferForm.watch("fromBranchId")}
                            onValueChange={(v) =>
                              transferForm.setValue("fromBranchId", v, {
                                shouldValidate: true,
                              })
                            }
                            placeholder="ເລືອກສາຂາຕົ້ນທາງ..."
                            className="h-10 w-full bg-muted/50"
                            clearable
                          />
                        ) : null}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                          ສາຂາປາຍທາງ
                        </Label>
                        {shopId ? (
                          <InfiniteCombobox<ShopBranchOption>
                            queryKey={[
                              ...branchQueryKey,
                              "to",
                              fromBranchId || "none",
                            ]}
                            queryFn={async ({ search: q, pageParam }) => {
                              if (!shopId) {
                                return { items: [], nextPage: null };
                              }
                              const res = await shopInventoryApi.lookupBranches(
                                shopId,
                                {
                                  page: pageParam,
                                  search: q.trim() || undefined,
                                  limit: 20,
                                },
                              );
                              const excludeId =
                                fromBranchId.length > 0 && isUuid(fromBranchId)
                                  ? fromBranchId
                                  : null;
                              const items = excludeId
                                ? res.items.filter((b) => b.id !== excludeId)
                                : res.items;
                              return { items, nextPage: res.nextPage };
                            }}
                            preloadQueryFn={(id) => {
                              if (!shopId) return Promise.resolve(null);
                              if (isUuid(fromBranchId) && id === fromBranchId) {
                                return Promise.resolve(null);
                              }
                              return shopInventoryApi.getBranch(shopId, id);
                            }}
                            getLabel={(item) => item.name}
                            getValue={(item) => item.id}
                            value={transferForm.watch("toBranchId")}
                            onValueChange={(v) =>
                              transferForm.setValue("toBranchId", v, {
                                shouldValidate: true,
                              })
                            }
                            placeholder="ເລືອກສາຂາປາຍທາງ..."
                            className="h-10 w-full bg-muted/50"
                            clearable
                          />
                        ) : null}

                        {warehouseLocationsOn &&
                        isUuid(transferForm.watch("toBranchId")) &&
                        shopId ? (
                          <div className="space-y-2">
                            <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                              ຊ່ອງປາຍທາງ (ທາງເລືອກ)
                            </Label>
                            <ShopWarehouseLocationPickerRemote
                              shopId={shopId}
                              branchId={transferForm.watch("toBranchId")}
                              value={transferForm.watch("toLocationId") ?? ""}
                              onValueChange={(v) =>
                                transferForm.setValue("toLocationId", v, {
                                  shouldValidate: true,
                                })
                              }
                              placeholder="default receive"
                              className="h-10 w-full bg-muted/50"
                            />
                          </div>
                        ) : null}

                        <FormTextarea
                          name="note"
                          label="ໝາຍເຫດພາຍໃນ"
                          placeholder="ອະທິບາຍເຫດຜົນການໂອນ (ເຊັ່ນ ເຕີມສາຂາຕາມລະດູ, ຄວາມຕ້ອງການດ່ວນ...)"
                          rows={3}
                        />
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 border-border border-t pt-4">
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">
                          ວັນທີເອກະສານ
                        </span>
                        <span className="font-medium text-card-foreground text-sm tabular-nums">
                          {dateLabel}
                        </span>
                      </div>
                    </div>
                  </section>

                  <div
                    className="flex gap-3 rounded-xl border border-secondary/30 bg-secondary px-4 py-3 text-secondary-foreground text-sm shadow-sm"
                    role="note"
                  >
                    <InfoIcon className="mt-0.5 size-5 shrink-0" aria-hidden />
                    <p className="leading-relaxed">
                      <span className="font-semibold uppercase tracking-wide">
                        ໝາຍເຫດລະບົບ
                      </span>
                      <span className="mt-1 block font-normal">
                        ທຸລະກຳນີ້ບັນທຶກຄູ່ movement:{" "}
                        <code className="rounded bg-black/10 px-1 font-mono text-[11px]">
                          transfer_out
                        </code>{" "}
                        ທີ່ຕົ້ນທາງ ແລະ{" "}
                        <code className="rounded bg-black/10 px-1 font-mono text-[11px]">
                          transfer_in
                        </code>{" "}
                        ທີ່ປາຍທາງ.
                      </span>
                    </p>
                  </div>
                </div>

                <section className="rounded-xl border border-border bg-card shadow-sm lg:col-span-7">
                  <div className="relative m-4">
                    <ScanBarcodeIcon
                      className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-primary"
                      aria-hidden
                    />
                    <Input
                      ref={barcodeInputRef}
                      id={barcodeFieldId}
                      value={barcode}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBarcode(v);
                        debouncedAutoScan(v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        void runScanForTransfer(e.currentTarget.value);
                      }}
                      placeholder="ລໍຖ້າສະແກນ..."
                      className="h-11 border-border pr-3 pl-10 font-mono text-sm"
                      autoComplete="off"
                      disabled={lookup.isPending}
                    />
                  </div>

                  <div className="overflow-x-auto px-2 pb-2 sm:px-0">
                    <table className="w-full min-w-[640px] text-left text-sm">
                      <thead>
                        <tr className="border-border border-b bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                          <th className="px-4 py-3 font-medium sm:pl-6">
                            ສິນຄ້າ / SKU
                          </th>
                          <th className="whitespace-nowrap px-3 py-3 font-medium">
                            ພ້ອມຂາຍ
                          </th>
                          <th className="w-28 whitespace-nowrap px-3 py-3 font-medium">
                            ຈຳນວນໂອນ
                          </th>
                          <th className="w-14 px-3 py-3 pr-4 text-center font-medium sm:pr-6">
                            ລົບ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineFields.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-10 text-center text-muted-foreground text-sm sm:px-6"
                            >
                              ຍັງບໍ່ມີລາຍການ — ສະແກນ barcode ຮ້ານຢູ່ຊ່ອງດ້ານເທິງເພື່ອເພີ່ມສິນຄ້າ
                            </td>
                          </tr>
                        ) : (
                          lineFields.map((field, index) => {
                            const skuId =
                              linesWatched[index]?.shopProductSkuId ?? "";
                            return (
                              <tr
                                key={field.id}
                                className="border-border border-b last:border-0"
                              >
                                <td className="max-w-xs px-4 py-3 align-top sm:pl-6">
                                  <TransferLineProductDisplay
                                    shopId={shopId}
                                    shopProductSkuId={skuId}
                                  />
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 align-middle">
                                  <LineAvailableQty
                                    shopId={shopId}
                                    fromBranchId={fromBranchId}
                                    shopProductSkuId={skuId}
                                    numberFmt={numberFmt}
                                  />
                                </td>
                                <td className="px-3 py-3 align-middle">
                                  <FormNumber
                                    name={`lines.${index}.qty`}
                                    min={1}
                                    className="h-10"
                                  />
                                </td>
                                <td className="px-3 py-3 pr-4 text-center align-middle sm:pr-6">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="size-9 text-destructive hover:text-destructive"
                                    onClick={() => removeLine(index)}
                                    aria-label="ລຶບແຖວ"
                                  >
                                    <Trash2Icon className="size-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <div className="flex flex-col-reverse gap-3 border-border border-t pt-6 sm:flex-row sm:justify-end">
                <Button
                  type="submit"
                  className="min-w-44 gap-2 font-semibold"
                  disabled={transferMut.isPending || !shopId}
                >
                  {transferMut.isPending ? (
                    <Loader />
                  ) : (
                    <ZapIcon className="size-4" aria-hidden />
                  )}
                  ຢືນຢັນໂອນ
                </Button>
              </div>
            </FormRoot>
          ) : (
            <p className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-muted-foreground text-sm">
              ບໍ່ມີສິດ <span className="font-mono text-xs">inventory.transfer</span>{" "}
              — ຕິດຕໍ່ຜູ້ຄຸມຮ້ານ
            </p>
          )}

          <p className="text-center text-muted-foreground text-xs">
            ຈອງ / ປົດຈອງສະຕ໋ອກ →{" "}
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-secondary"
              onClick={() => {
                if (!shopId) return;
                nav({
                  to: INVENTORY_RESERVE_ROUTE,
                  params: { shopId },
                });
              }}
            >
              ໜ້າຈອງ ແລະ ປົດຈອງ
            </Button>
          </p>
        </div>

        {scanErrorOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
              type="button"
              className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
              aria-label="ປິດ"
              onClick={() => setScanErrorOpen(false)}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="transfer-scan-error-title"
              className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-xl"
              onKeyDown={(e) => {
                if (e.key === "Escape") setScanErrorOpen(false);
              }}
            >
              <div
                className="absolute top-0 bottom-0 left-0 w-1 bg-destructive"
                aria-hidden
              />
              <div className="p-5 pl-6">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                    <AlertTriangle className="size-5" />
                  </div>
                  <div>
                    <h2
                      id="transfer-scan-error-title"
                      className="font-bold font-heading text-base text-card-foreground uppercase tracking-tight"
                    >
                      ສະແກບໍ່ສຳເລັດ
                    </h2>
                    <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                      ລະບົບບໍ່ພົບສິນຄ້ານີ້ໃນຖານຂໍ້ມູນສິນຄ້າຂອງຮ້ານນີ້
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="mb-1 font-bold text-[10px] text-destructive uppercase tracking-wider">
                    Barcode ທີ່ສະແກນ
                  </p>
                  <div className="flex items-center gap-2 rounded-lg border-2 border-destructive/40 bg-destructive/5 px-3 py-2.5">
                    <ScanBarcodeIcon className="size-5 shrink-0 text-destructive" />
                    <span className="min-w-0 flex-1 break-all font-mono text-card-foreground text-sm">
                      {failedBarcode}
                    </span>
                  </div>
                  <p className="mt-2 flex items-start gap-2 text-destructive text-xs">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      ບໍ່ພົບ barcode ນີ້ໃນຮ້ານ
                      {shopId ? ` (${shopIdShort}…)` : ""}. ກວດຄືນ ຫຼື
                      ຄົ້ນໃນລາຍການສິນຄ້າ
                    </span>
                  </p>
                </div>

                <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    className="h-10 flex-1 gap-2"
                    onClick={() => {
                      setScanErrorOpen(false);
                      queueMicrotask(() => barcodeInputRef.current?.focus());
                    }}
                  >
                    <RefreshCw className="size-4" />
                    ລອງສະແກນອີກ
                  </Button>
                  {shopId ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-10 flex-1 gap-2 bg-foreground text-background hover:bg-foreground/90"
                      onClick={() => {
                        setScanErrorOpen(false);
                        nav({
                          to: "/app/shops/$shopId/catalog",
                          params: { shopId },
                        });
                      }}
                    >
                      ໄປລາຍການສິນຄ້າ
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}

function TransferLineProductDisplay({
  shopId,
  shopProductSkuId,
}: {
  shopId: string | undefined;
  shopProductSkuId: string;
}) {
  const q = useQuery({
    queryKey: ["transfer-line-product", shopId, shopProductSkuId],
    queryFn: async () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.getSellableSku(shopId, shopProductSkuId);
    },
    enabled: Boolean(shopId && isUuid(shopProductSkuId)),
  });
  if (!shopProductSkuId || !isUuid(shopProductSkuId)) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (q.isLoading) {
    return (
      <div className="space-y-1.5">
        <div className="h-4 w-44 animate-pulse rounded bg-muted" />
        <div className="h-3 w-28 animate-pulse rounded bg-muted" />
      </div>
    );
  }
  const row = q.data;
  if (!row) {
    return <span className="text-muted-foreground text-xs">ບໍ່ພົບຂໍ້ມູນ</span>;
  }
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="font-medium text-card-foreground text-sm leading-snug">
        {row.productName}
      </p>
      <p className="font-mono text-muted-foreground text-xs">{row.shopSku}</p>
    </div>
  );
}
